import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';

import App from './App';
import Layout from './components/Layout';
import Marketplace from './pages/Marketplace';
import PlanDetails from './pages/PlanDetails';
import BundleDetails from './pages/BundleDetails';
import Processing from './pages/Processing';
import PaymentCallback from './pages/PaymentCallback';
import OrderDetails from './pages/OrderDetails';
import reportWebVitals from './reportWebVitals';
import { initAnalytics, page } from './utils/analytics';

/**
 * Routes.
 *
 * Partner entry points used to be a hand-maintained list of literal paths
 * (/visa, /visa1…/visa6, /perplexity, /zetapp, …), so onboarding a partner
 * needed a code change and a deploy — and any slug not on the list fell
 * through to react-router's default error screen, because there was no
 * catch-all either. `/:partnerSlug` covers all of them; react-router ranks
 * static segments above dynamic ones, so /marketplace and friends still win.
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
  },
  {
    element: <Layout />,
    children: [
      { path: '/marketplace', element: <Marketplace /> },
      { path: '/partnermarketplace', element: <Navigate to="/marketplace" replace /> },
      { path: '/usamarketplace', element: <Navigate to="/marketplace" replace /> },

      { path: '/plan-details/:planId', element: <PlanDetails /> },
      { path: '/plan/:planId', element: <PlanDetails /> },

      { path: '/bundle-details/:bundleId', element: <BundleDetails /> },
      { path: '/bundle/:bundleId', element: <BundleDetails /> },

      { path: '/processing', element: <Processing /> },
      { path: '/payment-callback', element: <PaymentCallback /> },

      { path: '/order-details', element: <OrderDetails /> },
      { path: '/order/:orderId', element: <OrderDetails /> },
    ],
  },
  {
    // Any other single segment is treated as a partner slug.
    path: '/:partnerSlug',
    element: <App />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

/**
 * One pageview per route change, for every route. Only the marketplace used
 * to report one. Pure redirects (<Navigate> routes) are skipped so
 * /partnermarketplace -> /marketplace counts once.
 */
initAnalytics();

let lastLocationKey = null;
const reportPageview = ({ location, matches }) => {
  if (!location || location.key === lastLocationKey) return;
  const leaf = matches?.[matches.length - 1]?.route;
  if (leaf?.element?.type === Navigate) return;
  // gtag('config') in index.html already counted the landing page for GA.
  const isLanding = lastLocationKey === null;
  lastLocationKey = location.key;
  page({ path: location.pathname, isLanding });
};

reportPageview(router.state);
router.subscribe(reportPageview);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

reportWebVitals();
